import React, { useEffect } from 'react';
import { Grid, Card, CardHeader, CardContent, CardActions, Divider, IconButton, Tooltip, TextField } from '@material-ui/core';
import { ReferenceInput, useGetOne, BooleanInput, useRedirect, required, DateInput } from 'react-admin';
import { Form, Field } from 'react-final-form';
import { get } from 'lodash';
import { Can, ACCESS } from '@/utils/auth';
import { MACHINES } from '@/vars/resources';
import routes from '@/vars/routes';
import { Page, Button, BackButton, DeleteButton } from '@/components';
import { NumberInput, AutocompleteInput } from '@/ra/input';
import ProductForm from '@/pages/machines/SlotEdit/ProductForm';
import { useGetSlot, useSaveSlot, useRemoveSlot, useGetBoardSlot, useDeleteSlotField, convertStringToTimestamp } from '../hooks';
import DeleteIcon from '@material-ui/icons/Delete';

const quantityExceedCapacity = msg => (value, values) => {
  const { maxCapacity } = values;
  if (value > maxCapacity) {
    return msg || 'Invalid';
  }
};

const SlotEditBoard = props => {
  const { match } = props;
  const { machineId, boardID, slotID } = match.params;
  const { data: machineRecord, loaded: machineLoaded } = useGetOne(MACHINES, machineId);
  const { data: slotRecord, loaded: slotLoaded } = useGetSlot(machineId, slotID);
  const { data: masterSchemaSlotData, loaded: masterSchemaLoaded } = useGetBoardSlot(machineId, boardID, slotID);
  const { saving, save } = useSaveSlot();
  const { deleteField } = useDeleteSlotField("expiryDate");
  const { removing, remove } = useRemoveSlot(machineId, slotID);
  const redirect = useRedirect();
  const isCreate = slotRecord ? false : true;

  const onSubmit = async formData => {
    let masterSchemaSlot = null;

    if (isCreate) {
      let formDataKeys = Object.keys(formData);
      masterSchemaSlot = masterSchemaSlotData;

      formDataKeys.forEach(key => {
        masterSchemaSlot[key] = formData[key];
      });

      masterSchemaSlot['machineSlot'] = parseInt(slotID);
    } else {
      masterSchemaSlot = formData;
    }

    if(!formData.expiryDate) {
      await deleteField(machineId, slotID);
      delete formData.expiryDate;
    } else {
      formData.expiryDate = convertStringToTimestamp(formData.expiryDate);
    }
    await save(machineId, masterSchemaSlot);
    redirect(routes.machines + '/' + machineId);
  };

  const onRemove = async () => {
    await remove();
    redirect(routes.machines + '/' + machineId);
  };

  const handleExpiryDate = (form) => {
    form.change('expiryDate', "");
  };

  if (!machineLoaded || !slotLoaded || !masterSchemaLoaded) {
    return null;
  }

  const machineSlot = masterSchemaSlotData.machineSlot || '';

  let dataToShow = null;

  if (isCreate) {
    dataToShow = masterSchemaSlotData;
  } else {
    dataToShow = slotRecord;
  }

  return (
    <Can module={ACCESS.VENDING_MACHINE}>
      <Page sectionTitle="Vending Machine" title={`Edit slot ${machineSlot}`}>
        {/* <Typography gutterBottom>Status: </Typography> */}
        <Card>
          <Form
            onSubmit={onSubmit}
            initialValues={{ dropSensor: true, ...dataToShow }}
            keepDirtyOnReinitialize
            validateOnBlur
          >
            {({ handleSubmit, values, form, dirtyFields, modified }) => {
              return (
                <form onSubmit={handleSubmit} noValidate>
                  <CardHeader title="Slot details" />
                  <CardContent>
                    <Grid container spacing={3}>
                      {/* <Grid item xs={12} md={6}>
                        <NumberInput source="machineSlot" defaultValue={parseInt(slotNumber)} disabled />
                      </Grid> */}
                      <Grid item xs={12} md={6}>
                        <NumberInput
                          label="Item number (display to user)"
                          source="itemNumber"
                          validate={required('Please enter an item number.')}
                        />
                        <Field component="input" type="hidden" name="machineSlot" initialValue={parseInt(slotID)} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <NumberInput source="maxCapacity" validate={required('Please enter a max capacity number.')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <NumberInput
                          label="Total quantity available"
                          source="totalQuantity"
                          validate={[
                            required('Please enter a quantity number.'),
                            quantityExceedCapacity('Quantity cannot exceed the max capacity.'),
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <NumberInput
                          label="Min capacity"
                          source="minCapacity"
                          validate={[quantityExceedCapacity('Min quantity cannot exceed the max capacity.')]}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                          <DateInput
                            label="Expiry Date"
                            source="expiryDate"
                            fullWidth
                            format={(value) => {
                              if (value && typeof value.toDate === 'function') {
                                return value.toDate().toISOString().split('T')[0];
                              }
                              return value;
                            }}
                            InputProps={{
                              inputProps: {
                                min: new Date().toISOString().split('T')[0],
                                style: { backgroundColor: 'white' }
                              }
                            }}
                          />
                          {values.expiryDate && (
                            <Grid item xs={12} md={2}>
                              <Tooltip title="Delete Expiry Date">
                                <IconButton onClick={() => handleExpiryDate(form)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          )}
                        </div>
                        {(() => {
                          const rawDate = values && values.expiryDate;
                          let expired = false;

                          if (rawDate) {
                            const expiry = new Date(rawDate.seconds ? rawDate.seconds * 1000 : rawDate);
                            const today = new Date();
                            expiry.setHours(0, 0, 0, 0);
                            today.setHours(0, 0, 0, 0);
                            expired = expiry < today;
                          }

                          if (expired) {
                            return (
                              <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px' }}>
                                Item is expired
                              </div>
                            );
                          }

                          return null;
                        })()}
                      </Grid>
                      <Grid item xs={12} md={6}></Grid>
                      <Grid item xs={12} md={6}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <BooleanInput
                            source="dropSensor"
                            label="Drop sensor"
                            formClassName="noFullWidth"
                          />
                          <BooleanInput
                            source="error"
                            label="Slot error"
                            formClassName="noFullWidth"
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardHeader title="Select product" />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <ReferenceInput
                          label="Product"
                          source="product.id"
                          reference="products"
                          filterToQuery={() => {}}
                          filter={{ client: get(machineRecord, 'client.id', '') }}
                          perPage={99999}
                          validate={required('Please select a product.')}
                        >
                          <AutocompleteInput optionText="name" />
                        </ReferenceInput>
                      </Grid>
                    </Grid>
                    {get(values, 'product.id') && (
                      <ProductForm
                        productId={get(values, 'product.id')}
                        formValues={values}
                        dirtyFields={dirtyFields}
                        modified={modified}
                        clientId={get(machineRecord, 'client.id')}
                        machineRecord={machineRecord}
                      />
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button type="submit" variant="contained" color="primary" loading={saving}>
                      Save
                    </Button>
                    <BackButton />
                    <DeleteButton onClick={onRemove} label="Remove" loading={removing} disabled={saving} />
                  </CardActions>
                </form>
              );
            }}
          </Form>
        </Card>
      </Page>
    </Can>
  );
};

export default SlotEditBoard;
